import { createContext, useContext, useState, useEffect, ReactNode } from "react";

import enTranslations from '../translations/en.json';
import frTranslations from '../translations/fr.json';
import deTranslations from '../translations/de.json';
import ruTranslations from '../translations/ru.json';
import roTranslations from '../translations/ro.json';
import itTranslations from '../translations/it.json';
import esTranslations from '../translations/es.json';
import trTranslations from '../translations/tr.json';
import arTranslations from '../translations/ar.json';

export type Language = "en" | "fr" | "de" | "ru" | "ro" | "it" | "es" | "tr" | "ar";

export const languages: { code: Language; name: string; nativeName: string; rtl?: boolean }[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "ro", name: "Romanian", nativeName: "Română" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  { code: "ar", name: "Arabic", nativeName: "العربية", rtl: true },
];

type TranslationValue = string | string[] | { [key: string]: TranslationValue };
type Translations = { [key: string]: TranslationValue };

const translationMap: Record<Language, Translations> = {
  en: enTranslations,
  fr: frTranslations,
  de: deTranslations,
  ru: ruTranslations,
  ro: roTranslations,
  it: itTranslations,
  es: esTranslations,
  tr: trTranslations,
  ar: arTranslations,
};

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string>) => string;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

function getNestedValue(obj: Translations, path: string): string {
  const keys = path.split(".");
  let value: TranslationValue = obj;
  
  for (const key of keys) {
    if (value && typeof value === "object" && !Array.isArray(value) && key in value) {
      value = value[key];
    } else {
      return path;
    }
  }
  
  return typeof value === "string" ? value : path;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get("lang") as Language;
      if (urlLang && languages.some(l => l.code === urlLang)) {
        localStorage.setItem("language", urlLang);
        return urlLang;
      }
      const saved = localStorage.getItem("language") as Language;
      if (saved && languages.some(l => l.code === saved)) {
        return saved;
      }
    }
    return "en";
  });

  const isRTL = languages.find(l => l.code === language)?.rtl ?? false;

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key: string, replacements?: Record<string, string>): string => {
    const translations = translationMap[language] || translationMap.en;
    let text = getNestedValue(translations, key);
    
    if (replacements) {
      Object.entries(replacements).forEach(([placeholder, value]) => {
        text = text.replace(new RegExp(`{{${placeholder}}}`, "g"), value);
      });
    }
    
    return text;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, isRTL }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }
  return context;
}
